import React from 'react';
import { Button, DialogActions, DialogContent, DialogTitle, LinearProgress, Dialog, Typography, DialogContentText } from '@mui/material';
import { Folder } from 'models';
import FolderHierarchy from 'components/FolderHierarchy';
import { useSelector } from 'react-redux';
import * as FolderStore from 'store/Folder';

export interface MoveItemDialogProps {
    onMove: (folder?: Folder) => Promise<string[] | undefined>;
    onValidate?: (folder?: Folder) => string[];
    onCancel?: () => void;
    message: string;
    title: string;
    currentFolderId: string;
}

export function MoveItemDialog(props: MoveItemDialogProps) {
    const [errors, setErrors] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [selectedFolderId, setSelectedFolderId] = React.useState<string>(props.currentFolderId);
    const folders = useSelector(FolderStore.selectors.foldersFlattened);
    const selectedFolder = folders.find(f => f.id === selectedFolderId);

    function validate(): boolean {
        setErrors([]);

        if (!selectedFolder && selectedFolderId !== 'root') {
            setErrors(['Please select a folder']);
            return false;
        }

        if (!props.onValidate) {
            return true;
        }

        setErrors(props.onValidate(selectedFolder));

        if (errors && errors.length > 0) {
            return false;
        }

        return true;
    }

    async function handleMove() {
        setLoading(true);
        if (validate()) {
            const saveErrors = await props.onMove(selectedFolder);
            if (saveErrors) {
                setErrors(saveErrors);
            }
            setLoading(false);
        } else {
            setLoading(false);
        }
    }

    function handleCancel() {
        if (props.onCancel) {
            props.onCancel();
        }
    }

    const handleNodeSelected = (nodeType: 'folder' | 'image', nodeId: string) => {
        switch (nodeType) {
            case 'folder': {
                setSelectedFolderId(nodeId);
                break;
            }
            default: {
                break; // only folders should be selected so this should never be hit
            }
        }
    }

    return (
        <Dialog open={true} fullWidth>
            <DialogTitle>{props.title}</DialogTitle>
            <DialogContent>
                {errors && errors.map(error => (
                    <Typography key={error} variant="body1" color="error">
                        {error}
                    </Typography>
                ))}
                {errors && errors.length > 0 && <br />}
                {loading === true && <LinearProgress />}
                <DialogContentText>
                    {props.message}
                </DialogContentText>
                <br />
                <FolderHierarchy foldersOnly includeRoot onNodeSelected={handleNodeSelected} selectedNodeId={selectedFolderId} />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleMove} color="primary" disabled={loading}>
                    Move
                </Button>
                <Button onClick={handleCancel} color="primary" disabled={loading}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>)
}